import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { DateRange } from '@angular/material/datepicker';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import {
  AutofocusDirective,
  ButtonComponent,
  ClearInvalidAutocompleteDirective,
  DialogService,
  IconComponent,
  RangeSelectorInputComponent,
  SpinnerComponent,
} from '@ird/ng-base';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import moment, { Moment } from 'moment';
import { isObservable, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { catchError, debounceTime, finalize, switchMap, tap } from 'rxjs/operators';
import { AllowedPipe } from '../../pipes';
import { FilterSearchInputComponent } from '../filter-search-input/filter-search-input.component';
import { FilterSearchParameters } from '../filter-search-input/filter-search-input.models';
import { SearchConfig, SearchConfigButton } from './models/search-config.model';
import { SearchParameters } from './models/search-parameters.model';
import { SearchOptionDirective } from './search-option.directive';

@Component({
  selector: 'shared-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  imports: [
    CommonModule,
    TranslatePipe,
    ButtonComponent,
    AllowedPipe,
    MatTooltip,
    IconComponent,
    SpinnerComponent,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    ReactiveFormsModule,
    MatAutocompleteModule,
    AutofocusDirective,
    MatSelect,
    MatSelectTrigger,
    RangeSelectorInputComponent,
    MatButtonToggleGroup,
    MatButtonToggle,
    FlexLayoutModule,
    ClearInvalidAutocompleteDirective,
    FilterSearchInputComponent,
  ],
})
export class SearchBarComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  public static readonly FILTER_DROPDOWN_THRESHOLD = 3;

  private destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService, { optional: true });
  private readonly translateService = inject(TranslateService);
  searchControl = new FormControl();
  filterValue: string | string[] = '';
  sortValue: string | undefined = '';
  dateRange: DateRange<Moment> | null;

  public get showFilterAsDropdown(): boolean {
    return (this.config?.filter?.options?.length ?? 0) > SearchBarComponent.FILTER_DROPDOWN_THRESHOLD;
  }

  public get selectedFilterLabels(): string[] {
    const filter = this.config?.filter;
    if (!filter) {
      return [];
    }
    let values: string[];
    if (Array.isArray(this.filterValue)) {
      values = this.filterValue;
    } else if (this.filterValue) {
      values = [this.filterValue];
    } else {
      values = [];
    }
    return filter.options.filter((o) => values.includes(o.value)).map((o) => o.label);
  }

  public filteredOptions!: Observable<Array<any>>;
  public loading = signal(false);
  public results = signal<Array<any>>([]);

  private parametersSubscription!: Subscription;
  private date!: {
    from: { key: string; value: string };
    to: { key: string; value: string };
  };

  @Input() initialData: any;
  @Input() config!: SearchConfig;
  @Input() initialParametersChange: ReplaySubject<SearchParameters> = new ReplaySubject<SearchParameters>();
  @Input() autofocusEnabled = true;
  @Input() searchString = '';

  @Output() parametersChanged: EventEmitter<SearchParameters> = new EventEmitter<SearchParameters>();
  @Output() searchInputChanged: EventEmitter<string> = new EventEmitter<string>();

  @ContentChild(SearchOptionDirective, { static: true }) optionTemplate?: SearchOptionDirective;

  @ViewChild('toggleGroup') private toggleGroup!: MatButtonToggleGroup;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.searchString) {
      this.searchControl.setValue(changes.searchString.currentValue);
    }
    if (changes.initialData?.currentValue !== changes.initialData?.previousValue && this.toggleGroup) {
      this.toggleGroup.value = this.filterValue;
    }
    if (changes.config && !changes.config.firstChange) {
      this.resyncFilterValueWithOptions();
    }
  }

  private resyncFilterValueWithOptions(): void {
    const options = this.config?.filter?.options ?? [];
    const availableValues = options.map((o) => o.value);
    let current: string[];
    if (Array.isArray(this.filterValue)) {
      current = this.filterValue;
    } else if (this.filterValue) {
      current = [this.filterValue];
    } else {
      current = [];
    }
    const stillAvailable = current.filter((v) => availableValues.includes(v));
    if (stillAvailable.length === current.length) {
      return;
    }
    this.filterValue = this.config?.filter?.multiple ? stillAvailable : (stillAvailable[0] ?? '');
    if (this.toggleGroup) {
      this.toggleGroup.value = this.filterValue;
    }
  }

  ngOnInit(): void {
    if (!this.config) {
      return;
    }

    if (this.config.search?.value) {
      this.searchControl.setValue(this.config.search?.value);
    }

    this.sortValue = this.config.sortOptions?.[0].value;

    if (this.config.filter?.value) {
      this.filterValue = this.config.filter.value;
    }

    if (this.config.search?.autocomplete) {
      this.initAutocomplete();
    } else if (this.config.search?.smartFilter === false) {
      // Free-text mode: emit only on explicit submit (Enter or button click), not on each keystroke.
      this.searchControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
        this.searchInputChanged.next(value);
      });
    } else if (this.parametersChanged) {
      this.searchControl.valueChanges
        .pipe(
          tap((value) => {
            this.searchInputChanged.next(value);
          }),
          debounceTime(this.config?.search?.searchDebounceTime ?? 400),
        )
        .subscribe(() => {
          this.computeParameters();
        });
    }

    if (this.initialParametersChange) {
      this.parametersSubscription = this.initialParametersChange.subscribe((params) => {
        if (params.filter) {
          const nextFilterValue = this.config?.filter?.multiple ? params.filter.map((f) => f.value) : params.filter[0].value;
          this.filterValue = nextFilterValue;
          if (this.toggleGroup) {
            this.toggleGroup.value = nextFilterValue;
          }
        } else if (params.search) {
          this.searchControl.setValue(params.search, {
            emitEvent: true,
          });
        }
      });
    }

    if (this.config.dateFilter) {
      if (this.config.dateFilter.startValue && this.config.dateFilter.endValue) {
        const start = moment(this.config.dateFilter.startValue / 1000000);
        const end = moment(this.config.dateFilter.endValue / 1000000);
        this.dateRange = new DateRange(start, end);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.config.filter?.value && this.toggleGroup) {
      this.toggleGroup.value = this.filterValue;
    }
  }

  ngOnDestroy() {
    if (this.parametersSubscription) {
      this.parametersSubscription.unsubscribe();
    }
  }

  initAutocomplete(): void {
    this.filteredOptions = this.searchControl.valueChanges.pipe(
      tap((value) => {
        this.searchInputChanged.next(value);
        this.loading.set(true);
      }),
      debounceTime(500),
      switchMap((value) => {
        const loader = this.config.search?.dataLoader ? this.config.search?.dataLoader(value, 0, 50) : of([]);
        return loader.pipe(
          catchError(() => of<Array<unknown>>([])),
          finalize(() => this.loading.set(false)),
        );
      }),
    );

    this.filteredOptions.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((items) => {
      this.results.set(items ?? []);
    });
  }

  getElementValue(element: any) {
    if (this.config?.search && typeof this.config.search.variableName === 'string') {
      return element[this.config.search.variableName];
    } else if (this.config?.search?.variableName && Array.isArray(this.config?.search?.variableName)) {
      return element[
        this.config.search.variableName.find((item) => {
          return element[item]?.toLowerCase().includes(this.searchControl.value?.toLowerCase());
        })
      ];
    }
  }

  sort(sort: string) {
    this.sortValue = sort;
    this.computeParameters();
  }

  toggleFilter() {
    const toggleValue = this.toggleGroup.value;
    if (toggleValue) {
      if (toggleValue === this.filterValue) {
        if (this.config.filter.required) {
          //Do not recompute the parameters again.
          return;
        }
        this.toggleGroup.value = '';
        this.filterValue = null;
      } else {
        this.toggleGroup.value = toggleValue;
        this.filterValue = toggleValue;
      }
    }
    this.computeParameters();
  }

  onFilterSelectChange(value: string | string[]): void {
    this.filterValue = value ?? (this.config?.filter?.multiple ? [] : '');
    this.computeParameters();
  }

  clearSearchField(): void {
    this.searchControl.reset(undefined, { emitEvent: true });

    if (this.config?.search?.autocomplete) {
      this.computeParameters();
    }
  }

  onButtonAction(button: SearchConfigButton): void {
    if (button.loading || button.disabled) {
      return;
    }

    const result = button.action();

    if (result instanceof Subscription) {
      button.loading = true;
      result.add(() => {
        button.loading = false;
      });
    } else if (isObservable(result)) {
      button.loading = true;
      result.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        error: () => {
          button.loading = false;
        },
        complete: () => {
          button.loading = false;
        },
      });
    } else if (result instanceof Promise) {
      button.loading = true;
      result.finally(() => {
        button.loading = false;
      });
    }
  }

  computeParameters(): void {
    const sort = this.sortValue ? this.sortValue.split('-') : [];
    const sortKey = sort[0] ?? '';
    const sortDirection = sort[1] ?? '';

    const parameters: SearchParameters = {
      search: this.searchControl.value,

      sort: {
        dir: sortDirection,
        key: sortKey,
      },
    };

    if (this.config?.filter?.multiple) {
      parameters.filter = (Array.isArray(this.filterValue) ? this.filterValue : [this.filterValue]).map((value) => {
        return {
          value,
        };
      });
    } else {
      parameters.filter = [
        {
          value: this.filterValue as string,
        },
      ];
    }

    if (this.date?.to) {
      parameters.filter.push(this.date.to);
    }

    if (this.date?.from) {
      parameters.filter.push(this.date.from);
    }

    this.parametersChanged.next(parameters);
  }

  chooseAutocompleteElement(search: string): void {
    this.searchControl.setValue(search);
    this.computeParameters();
  }

  onFreeTextSubmit(): void {
    this.computeParameters();
  }

  openFreeTextHelp(): void {
    this.dialogService?.openDialog(
      this.translateService.instant('GENERIC_ADMIN.SEARCH_HELP.FREE_TEXT_BODY'),
      this.translateService.instant('GENERIC_ADMIN.SEARCH_HELP.TITLE'),
    );
  }

  onFilterSearchChanged(params: FilterSearchParameters): void {
    const sort = this.sortValue ? this.sortValue.split('-') : [];
    const sortKey = sort[0] ?? '';
    const sortDirection = sort[1] ?? '';

    const parameters: SearchParameters = {
      search: params.search,
      filterCriteria: params.filters,
      sort: {
        dir: sortDirection,
        key: sortKey,
      },
    };

    if (this.config?.filter?.multiple) {
      parameters.filter = (Array.isArray(this.filterValue) ? this.filterValue : [this.filterValue]).map((value) => ({
        value,
      }));
    } else {
      parameters.filter = [{ value: this.filterValue as string }];
    }

    this.parametersChanged.next(parameters);
  }

  dateRangeChanged(dateRange: DateRange<Moment>): void {
    this.date = {
      from: { key: 'from', value: String(dateRange.start.valueOf() * 1000000) },
      to: { key: 'to', value: String(dateRange.end.valueOf() * 1000000) },
    };
    this.computeParameters();
  }
}
